import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Camera, CheckCircle, Circle, Warning, X, PaperPlaneTilt,
  FlagBanner, MapPin, ClipboardText, CaretRight, MagicWand,
  Microphone, Stop, Trash,
} from "@phosphor-icons/react";
import PurgeFlagModal from "@/components/PurgeFlagModal";
import WalkV2 from "@/pages/WalkV2";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * InspectDetail — the tech mobile flow for one Property Inspection.
 *
 * Route: /inspect/:id
 * Views: (1) room list · (2) per-step camera OR Q&A wizard.
 */
export default function InspectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const [i, setI] = useState(null);
  const [cursor, setCursor] = useState(null); // {roomIdx, stepIdx} or null (room list)

  const load = async () => {
    try {
      const r = await axios.get(`${BASE}/api/inspections/${id}`, auth());
      setI(r.data);
    } catch { toast.error("Couldn't load inspection"); nav("/admin/property-inspections"); }
  };
  useEffect(() => { load(); }, [id]);

  if (!i) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (i.walk_version === 2) return <WalkV2 mode="staff" id={id} onExit={() => nav("/admin/property-inspections")}/>;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {cursor === null
        ? <RoomList i={i} onOpen={(r, s) => setCursor({ roomIdx: r, stepIdx: s })} onBack={() => nav(`/admin/property-inspections`)} reload={load}/>
        : <StepWizard i={i} cursor={cursor} setCursor={setCursor} reload={load}/>
      }
    </div>
  );
}


function RoomList({ i, onOpen, onBack, reload }) {
  const [showSig, setShowSig] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const canSubmit = i.progress.pct === 100 && i.status !== "submitted";
  const alreadySubmitted = i.status === "submitted";
  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b-2 border-slate-200 p-3 flex items-center gap-3">
        <button onClick={onBack} className="w-10 h-10 flex items-center justify-center hover:bg-slate-100 -ml-2"
          data-testid="inspect-back">
          <ArrowLeft size={18}/>
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-black uppercase tracking-widest text-violet-600">{i.template_name}</div>
          <div className="font-black text-slate-900 truncate">{i.property_address}</div>
        </div>
        <div className="text-right flex-none">
          <div className="text-2xl font-black tabular-nums text-violet-700">{i.progress.pct}%</div>
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">complete</div>
        </div>
      </div>
      {/* Progress */}
      <div className="h-1.5 bg-slate-200">
        <div className="h-full bg-violet-500 transition-all" style={{ width: `${i.progress.pct}%` }}/>
      </div>
      {alreadySubmitted && (
        <div className="mx-4 mt-4 p-3 border-2 border-emerald-500 bg-emerald-50 text-emerald-800 text-sm font-bold inline-flex items-center gap-2">
          <CheckCircle weight="fill" size={16}/> Submitted &amp; signed — PDF is ready to download.
        </div>
      )}
      {canSubmit && (
        <div className="mx-4 mt-4 p-3 border-2 border-emerald-500 bg-emerald-50">
          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800">🎉 All steps complete</div>
          <div className="mt-1 text-sm text-slate-800">
            Capture a signature to lock the report and generate the PDF.
          </div>
          <button onClick={() => setShowSig(true)} data-testid="submit-with-sig-btn"
            className="mt-2 w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5">
            <PaperPlaneTilt weight="fill" size={14}/> Sign &amp; Submit
          </button>
        </div>
      )}
      {/* Flag as Bulk Trash · Truck Day — always available */}
      <div className="mx-4 mt-3">
        <button
          type="button"
          data-testid="inspect-flag-purge-btn"
          onClick={() => setPurgeOpen(true)}
          className="w-full h-11 border-2 border-amber-500 bg-amber-50 hover:bg-amber-100 text-amber-900 font-black uppercase tracking-widest text-xs rounded-md flex items-center justify-center gap-2">
          <Trash weight="fill" size={14}/> Flag Bulk Trash · Truck Day
        </button>
      </div>
      <PurgeFlagModal
        open={purgeOpen}
        onClose={() => setPurgeOpen(false)}
        defaultAddress={i.property_address || ""}
        inspectionId={i.id || i._id || null}
      />
      {/* Room list */}
      <div className="p-4 space-y-2">
        {i.rooms.map((room, rIdx) => {
          const total = room.steps.length;
          const done = room.steps.filter(s => s.completed_at || s.na).length;
          const flagged = room.steps.filter(s => s.flagged).length;
          const allDone = total > 0 && done === total;
          // First step that isn't done — where a tap on the header should
          // deep-link the tech. Falls back to step 0 for fully-completed
          // rooms so they can still review their work.
          const firstOpenIdx = room.steps.findIndex(s => !s.completed_at && !s.na);
          const jumpIdx = firstOpenIdx === -1 ? 0 : firstOpenIdx;
          return (
            <div key={rIdx} className="border-2 border-slate-200 bg-white" data-testid={`room-${rIdx}`}>
              <button type="button" onClick={() => onOpen(rIdx, jumpIdx)}
                data-testid={`room-header-${rIdx}`}
                className="w-full p-3 flex items-center gap-2 border-b border-slate-100 hover:bg-violet-50 text-left transition-colors">
                {allDone
                  ? <CheckCircle weight="fill" size={18} className="text-emerald-600"/>
                  : <Circle size={18} className="text-slate-300"/>
                }
                <div className="flex-1 min-w-0">
                  <div className="font-black text-slate-900">{room.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {done} of {total} steps
                    {flagged > 0 && <span className="ml-2 text-amber-700 font-bold">· {flagged} flagged</span>}
                  </div>
                </div>
                <CaretRight size={16} className="text-violet-500 flex-none"/>
              </button>
              <div className="divide-y divide-slate-100">
                {room.steps.map((step, sIdx) => {
                  const isDone = !!step.completed_at || !!step.na;
                  const kind = step.kind;
                  const photoCount = (step.photos || []).length;
                  return (
                    <button key={sIdx} onClick={() => onOpen(rIdx, sIdx)}
                      data-testid={`step-${rIdx}-${sIdx}`}
                      className={`w-full p-3 flex items-center gap-3 text-left hover:bg-slate-50 ${isDone ? "opacity-70" : ""}`}>
                      {isDone
                        ? <CheckCircle weight="fill" size={16} className="text-emerald-600 flex-none"/>
                        : <Circle size={16} className="text-slate-300 flex-none"/>
                      }
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-slate-800">
                          {kind === "photos" ? "📸 Photos" : "✅ Q&A"}
                          {step.na && <span className="ml-2 text-[10px] font-black uppercase tracking-widest text-slate-500">N/A</span>}
                        </div>
                        {step.prompt && <div className="text-[11px] text-slate-500 line-clamp-1">{step.prompt}</div>}
                        {kind === "photos" && photoCount > 0 && (
                          <div className="text-[10px] text-cyan-700 font-bold">{photoCount} photo{photoCount === 1 ? "" : "s"} captured</div>
                        )}
                        {step.flagged && (
                          <div className="text-[10px] text-amber-700 font-bold inline-flex items-center gap-0.5">
                            <FlagBanner size={10} weight="fill"/> Flagged for maintenance
                          </div>
                        )}
                      </div>
                      <CaretRight size={14} className="text-slate-400 flex-none"/>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {showSig && <SignatureModal inspection={i} onClose={() => setShowSig(false)} onSubmitted={() => { setShowSig(false); reload?.(); }}/>}
    </div>
  );
}


function StepWizard({ i, cursor, setCursor, reload }) {
  const room = i.rooms[cursor.roomIdx];
  const step = room.steps[cursor.stepIdx];
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [answers, setAnswers] = useState(step.answers || {});
  const [notes, setNotes] = useState(step.notes || "");
  const [showFlag, setShowFlag] = useState(false);

  useEffect(() => {
    setAnswers(step.answers || {});
    setNotes(step.notes || "");
  }, [cursor.roomIdx, cursor.stepIdx]);

  const totalSteps = useMemo(
    () => i.rooms.reduce((n, r) => n + r.steps.length, 0),
    [i.rooms]
  );
  const linearIdx = useMemo(() => {
    let idx = 0;
    for (let r = 0; r < cursor.roomIdx; r++) idx += i.rooms[r].steps.length;
    idx += cursor.stepIdx + 1;
    return idx;
  }, [cursor, i.rooms]);

  const next = () => {
    if (cursor.stepIdx + 1 < room.steps.length) {
      setCursor({ roomIdx: cursor.roomIdx, stepIdx: cursor.stepIdx + 1 });
    } else if (cursor.roomIdx + 1 < i.rooms.length) {
      setCursor({ roomIdx: cursor.roomIdx + 1, stepIdx: 0 });
    } else {
      setCursor(null);   // back to room list, done
    }
  };

  const uploadPhoto = async (files) => {
    const arr = Array.from(files);
    if (!arr.length) return;
    const roomIdx = cursor.roomIdx;
    const stepIdx = cursor.stepIdx;
    setUploading(true);
    setUploadProgress({ done: 0, total: arr.length });
    let done = 0;
    let failures = 0;
    // Parallel uploads with a concurrency cap — techs can queue a whole
    // gallery selection and see them all pushed at once instead of waiting
    // for each single upload to round-trip.
    const CONCURRENCY = 4;
    let next = 0;
    const worker = async () => {
      while (next < arr.length) {
        const idx = next++;
        const file = arr[idx];
        try {
          const fd = new FormData();
          fd.append("room_idx", String(roomIdx));
          fd.append("step_idx", String(stepIdx));
          fd.append("file", file);
          await axios.post(`${BASE}/api/inspections/${i.id}/step/photo`, fd, {
            headers: { Authorization: `Bearer ${token()}`, "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
        } catch (e) {
          failures++;
        } finally {
          done++;
          setUploadProgress({ done, total: arr.length });
        }
      }
    };
    await Promise.all(new Array(Math.min(CONCURRENCY, arr.length)).fill(0).map(() => worker()));
    setUploading(false);
    setUploadProgress({ done: 0, total: 0 });
    if (failures) toast.error(`${failures} of ${arr.length} photo${arr.length === 1 ? "" : "s"} failed`);
    reload();
  };

  const saveAndAdvance = async ({ na, completed } = {}) => {
    try {
      await axios.patch(`${BASE}/api/inspections/${i.id}/step`, {
        room_idx: cursor.roomIdx,
        step_idx: cursor.stepIdx,
        answers: step.kind === "qa" ? answers : undefined,
        notes: notes || undefined,
        na: na !== undefined ? na : undefined,
        completed: completed !== undefined ? completed : true,
      }, auth());
      await reload();
      next();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="sticky top-0 z-10 bg-white border-b-2 border-slate-200 p-3 flex items-center gap-3">
        <button onClick={() => setCursor(null)} className="w-10 h-10 flex items-center justify-center hover:bg-slate-100 -ml-2"
          data-testid="step-back">
          <X size={18}/>
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-black uppercase tracking-widest text-violet-600">
            Step {linearIdx} of {totalSteps} · {room.name}
          </div>
          <div className="text-sm font-black text-slate-900 truncate">
            {step.kind === "photos" ? "Photos" : "Q&A Checklist"}
          </div>
        </div>
        <button onClick={() => setShowFlag(true)}
          data-testid="flag-btn"
          className="h-9 px-2 border-2 border-amber-400 text-amber-800 hover:bg-amber-50 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          <FlagBanner weight="fill" size={12}/> Flag
        </button>
      </div>

      <div className="p-4">
        {step.prompt && (
          <div className="p-3 bg-violet-50 border-l-4 border-violet-500 text-sm text-slate-800 italic">
            {step.prompt}
          </div>
        )}

        {step.kind === "photos" && (
          <div className="mt-4 space-y-3">
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              data-testid="capture-btn"
              className="w-full h-40 border-4 border-dashed border-cyan-400 bg-cyan-50 hover:bg-cyan-100 flex flex-col items-center justify-center disabled:opacity-40">
              <Camera weight="fill" size={44} className="text-cyan-600"/>
              <div className="mt-2 font-black uppercase tracking-widest text-sm text-cyan-800">
                {uploading
                  ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…`
                  : "Add photos"}
              </div>
              <div className="text-[11px] text-slate-600">
                {uploading ? "Multi-upload in flight — leave this page open" : "Tap to open camera or gallery · multi-select supported"}
              </div>
            </button>
            <input ref={fileRef} type="file" multiple accept="image/*" capture="environment"
              className="hidden" data-testid="capture-input"
              onChange={(e) => { if (e.target.files?.length) { uploadPhoto(e.target.files); e.target.value = ""; }}}/>
            {(step.photos || []).length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="captured-photos">
                {step.photos.map((p, k) => (
                  <PhotoTile key={k} p={p} inspectionId={i.id}
                    roomIdx={cursor.roomIdx} stepIdx={cursor.stepIdx} photoIdx={k}
                    onAnalyzed={reload}
                    onFlag={(title, description, trade) => {
                      setShowFlag(true);
                      // Pre-fill via a mounted-modal ref would be cleaner; for now
                      // the user re-enters. AI title still saved on the photo.
                    }}/>
                ))}
              </div>
            )}
          </div>
        )}

        {step.kind === "qa" && (
          <div className="mt-4 space-y-2">
            {(step.items || []).map((q, k) => (
              <div key={k} className="p-3 border-2 border-slate-200 bg-white">
                <div className="text-sm font-bold text-slate-800">{q}</div>
                <div className="mt-2 flex gap-2">
                  {["yes", "no", "na"].map(v => (
                    <button key={v}
                      onClick={() => setAnswers(a => ({ ...a, [q]: v }))}
                      data-testid={`qa-${k}-${v}`}
                      className={`flex-1 h-9 border-2 font-black uppercase text-[10px] tracking-widest ${
                        answers[q] === v
                          ? v === "yes" ? "bg-emerald-600 border-emerald-700 text-white"
                            : v === "no" ? "bg-rose-600 border-rose-700 text-white"
                            : "bg-slate-600 border-slate-700 text-white"
                          : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"
                      }`}>
                      {v.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Notes (optional)</label>
            <VoiceMic onText={(t) => setNotes(n => (n ? n + " " : "") + t)} auth={auth}/>
          </div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            data-testid="step-notes"
            placeholder="Anything worth remembering… or tap the mic to dictate"
            className="mt-1 w-full h-20 p-2 border-2 border-slate-300 text-sm"/>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t-2 border-slate-900 bg-white p-3 flex gap-2 z-20">
        <button onClick={() => saveAndAdvance({ na: true, completed: true })}
          data-testid="na-btn"
          className="flex-1 h-11 border-2 border-slate-300 hover:bg-slate-100 font-black uppercase text-xs tracking-widest">
          N/A · Skip
        </button>
        <button onClick={() => saveAndAdvance({ completed: true })}
          data-testid="next-btn"
          className="flex-[2] h-11 bg-violet-600 hover:bg-violet-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5">
          Next Step <CaretRight weight="bold" size={14}/>
        </button>
      </div>

      {showFlag && <FlagModal
        onClose={() => setShowFlag(false)}
        onSubmit={async ({ title, description, trade }) => {
          try {
            const r = await axios.post(`${BASE}/api/inspections/${i.id}/step/flag`, {
              room_idx: cursor.roomIdx, step_idx: cursor.stepIdx,
              title, description, trade,
            }, auth());
            toast.success(`WO created — auto-attached this step's photos`);
            setShowFlag(false);
            await reload();
          } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Flag failed"));
          }
        }}
        defaultTitle={`${room.name} — issue`}
      />}
    </div>
  );
}


function PhotoTile({ p, inspectionId, roomIdx, stepIdx, photoIdx, onAnalyzed }) {
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });
  const [analyzing, setAnalyzing] = useState(false);
  const ai = p.ai_analysis;

  const analyze = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setAnalyzing(true);
    try {
      const r = await axios.post(`${BASE}/api/inspections/${inspectionId}/step/photo/analyze`,
        { room_idx: roomIdx, step_idx: stepIdx, photo_idx: photoIdx }, auth());
      if (r.data.damages?.length) {
        toast.success(`AI found ${r.data.damages.length} damage item(s)`);
      } else {
        toast.info("AI: no damage detected");
      }
      onAnalyzed?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail ?? "AI analysis failed"));
    } finally { setAnalyzing(false); }
  };

  const sevColor = {
    severe: "border-rose-500 bg-rose-500/20",
    moderate: "border-amber-500 bg-amber-500/20",
    minor: "border-yellow-500 bg-yellow-500/20",
  };

  return (
    <div className="relative aspect-square border-2 border-slate-200 overflow-hidden group"
      data-testid={`photo-tile-${photoIdx}`}>
      <img src={`${BASE}/api/inspections/walk-photo/${p.path}?auth=${encodeURIComponent(localStorage.getItem("fr_token") || localStorage.getItem("token") || "")}`}
        alt="" className="w-full h-full object-cover"/>
      {/* AI damage box overlays */}
      {ai?.damages?.map((d, k) => d.box && (
        <div key={k}
          className={`absolute border-2 ${sevColor[d.severity] || "border-fuchsia-500 bg-fuchsia-500/20"}`}
          style={{
            left: `${(d.box.x || 0) * 100}%`,
            top: `${(d.box.y || 0) * 100}%`,
            width: `${(d.box.w || 0) * 100}%`,
            height: `${(d.box.h || 0) * 100}%`,
          }}
          title={`${d.label} · ${d.severity} · ${d.trade}`}
        />
      ))}
      {/* Analyze button */}
      <button onClick={analyze} disabled={analyzing}
        data-testid={`analyze-btn-${photoIdx}`}
        className={`absolute bottom-1 right-1 h-6 px-1.5 text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-0.5 ${
          ai ? "bg-emerald-600 text-white" : "bg-slate-900/80 hover:bg-fuchsia-600 text-white"
        } disabled:opacity-60`}>
        <MagicWand size={9} weight="fill"/> {analyzing ? "…" : ai ? "AI ✓" : "AI"}
      </button>
      {/* Severity badge */}
      {ai?.overall_severity && ai.overall_severity !== "none" && (
        <span className={`absolute top-1 left-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${
          ai.overall_severity === "severe" ? "bg-rose-600"
            : ai.overall_severity === "moderate" ? "bg-amber-600" : "bg-yellow-500"
        } text-white`}>
          {ai.overall_severity}
        </span>
      )}
    </div>
  );
}


function FlagModal({ onClose, onSubmit, defaultTitle }) {
  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState("");
  const [trade, setTrade] = useState("general");
  return (
    <div className="fixed inset-0 z-[9999] bg-slate-950/70 flex items-center justify-center p-4"
      onClick={onClose} data-testid="flag-modal">
      <div onClick={e => e.stopPropagation()}
        className="bg-white border-4 border-amber-500 w-full max-w-md p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
            <FlagBanner weight="fill" size={20} className="text-amber-600"/>
            Flag for Maintenance
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 text-2xl">×</button>
        </div>
        <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 p-2">
          This creates a new Field Radar Work Order with this step's photos attached. Zero double-entry.
        </div>
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">What's wrong?</label>
          <input value={title} onChange={e => setTitle(e.target.value)}
            data-testid="flag-title"
            className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
        </div>
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Details</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)}
            data-testid="flag-description"
            placeholder="e.g. Faucet won't stop dripping, needs new cartridge"
            className="mt-1 w-full h-20 p-2 border-2 border-slate-300 text-sm"/>
        </div>
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Trade</label>
          <select value={trade} onChange={e => setTrade(e.target.value)}
            data-testid="flag-trade"
            className="mt-1 w-full h-10 px-2 border-2 border-slate-300 text-sm">
            {["general", "plumbing", "electric", "hvac", "carpentry", "appliance", "paint", "cleaning"].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => onSubmit({ title, description, trade })}
          data-testid="flag-create-btn"
          disabled={!title.trim()}
          className="w-full h-11 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest">
          Create Work Order
        </button>
      </div>
    </div>
  );
}



/**
 * VoiceMic — tap-and-hold (or click-to-toggle) mic. Records audio via
 * MediaRecorder → POSTs to /api/ai/transcribe → appends to the parent
 * notes textarea via onText.
 */
function VoiceMic({ onText, auth }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const BASE_ = process.env.REACT_APP_BACKEND_URL;

  const start = async () => {
    if (recording || busy) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice input not supported on this device");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 500) return;
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const r = await axios.post(`${BASE_}/api/ai/transcribe`, fd, {
            headers: { ...auth().headers, "Content-Type": "multipart/form-data" },
            timeout: 30000,
          });
          if (r.data.text) onText?.(r.data.text);
        } catch (e) {
          toast.error("Transcription failed");
        } finally { setBusy(false); }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied");
    }
  };

  const stop = () => {
    try { mediaRef.current?.stop(); } catch {}
    setRecording(false);
  };

  return (
    <button type="button"
      onClick={recording ? stop : start}
      disabled={busy}
      data-testid="voice-mic-btn"
      title={recording ? "Tap to stop" : "Tap to dictate"}
      className={`h-8 px-2 border-2 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest ${
        busy ? "bg-slate-200 border-slate-300 text-slate-500"
        : recording ? "bg-rose-600 border-rose-700 text-white animate-pulse"
        : "bg-white border-fuchsia-400 text-fuchsia-700 hover:bg-fuchsia-50"
      }`}>
      {busy ? "…" : recording ? <><Stop weight="fill" size={11}/> Stop</> : <><Microphone weight="fill" size={11}/> Dictate</>}
    </button>
  );
}


/**
 * SignatureModal — finger/mouse signature capture on a HTML5 canvas.
 * Sends the base64 data URL to POST /api/inspections/{id}/submit which
 * flips the inspection to `submitted` + persists the signature for the PDF.
 */
function SignatureModal({ inspection, onClose, onSubmitted }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasStroke, setHasStroke] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [busy, setBusy] = useState(false);
  const BASE_ = process.env.REACT_APP_BACKEND_URL;
  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // Retina-crisp canvas
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.2;
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0];
    const x = (t?.clientX ?? e.clientX) - r.left;
    const y = (t?.clientY ?? e.clientY) - r.top;
    return { x, y };
  };
  const down = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(e);
    setHasStroke(true);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  };
  const up = () => { drawingRef.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasStroke(false);
  };

  const submit = async () => {
    if (!hasStroke) { toast.error("Please sign first"); return; }
    setBusy(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await axios.post(`${BASE_}/api/inspections/${inspection.id}/submit`,
        { signature_data_url: dataUrl, signer_name: signerName || undefined },
        { headers: { Authorization: `Bearer ${token()}` } });
      toast.success("Signed & submitted");
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Submit failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-950/70 flex items-center justify-center p-4"
      data-testid="signature-modal">
      <div className="bg-white border-4 border-emerald-500 w-full max-w-lg p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-black text-xl uppercase inline-flex items-center gap-2">
            <PaperPlaneTilt weight="fill" size={18} className="text-emerald-600"/> Sign &amp; Submit
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 text-2xl">×</button>
        </div>
        <div className="text-xs text-slate-600 bg-emerald-50 border border-emerald-200 p-2">
          Sign below to lock the report. This signature will appear on the final PDF.
        </div>
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Signer name (optional)</label>
          <input value={signerName} onChange={e => setSignerName(e.target.value)}
            data-testid="sig-name"
            placeholder="e.g., Andrew Harman"
            className="mt-1 w-full h-9 px-2 border-2 border-slate-300 text-sm"/>
        </div>
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Signature</label>
          <div className="mt-1 border-2 border-slate-900 bg-white touch-none">
            <canvas ref={canvasRef}
              data-testid="sig-canvas"
              className="w-full h-40 cursor-crosshair"
              onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}
              onTouchStart={down} onTouchMove={move} onTouchEnd={up}/>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-[10px] text-slate-500">Sign with finger or mouse</span>
            <button onClick={clear} data-testid="sig-clear"
              className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-rose-600">
              Clear
            </button>
          </div>
        </div>
        <button onClick={submit} disabled={busy || !hasStroke}
          data-testid="sig-submit"
          className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5">
          <PaperPlaneTilt weight="fill" size={14}/>
          {busy ? "Submitting…" : "Sign &amp; Submit"}
        </button>
      </div>
    </div>
  );
}
